import React, { useState, useEffect, useCallback } from 'react';
import {
  Images,
  Plus,
  Trash2,
  Star,
  Edit2,
  AlertCircle,
} from 'lucide-react';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Badge from '../components/Badge';
import ImageUploader from '../components/ImageUploader';
import { useToast } from '../components/Toast';
import { queries } from '../../lib/supabase/queries';
import { storage } from '../../lib/supabase/storage';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Residential',
    image_url: '',
    storage_path: '',
    featured: false,
    status: 'published',
    sort_order: 0,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [formSaving, setFormSaving] = useState(false);

  const toast = useToast();

  const categories = [
    'Residential',
    'Commercial',
    'Renovation',
    'Living Room',
    'Modular Kitchen',
    'Luxury Wardrobe',
    'Pooja Unit',
    'TV & Entertainment',
    'Crockery Unit',
  ];

  const loadGallery = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setError('Supabase connection is not configured in .env.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await queries.gallery.getAll();
      setItems(data);
    } catch (err) {
      console.error('Error loading gallery:', err);
      setError(err.message || 'Failed to fetch gallery items from Supabase.');
      toast.error('Failed to load gallery: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const handleOpenCreate = () => {
    setIsEditing(false);
    setSelectedItem(null);
    setSelectedFile(null);
    setFormData({
      title: '',
      description: '',
      category: 'Residential',
      image_url: '',
      storage_path: '',
      featured: false,
      status: 'published',
      sort_order: items.length + 1,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (item) => {
    setIsEditing(true);
    setSelectedItem(item);
    setSelectedFile(null);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      category: item.category || 'Residential',
      image_url: item.image_url || '',
      storage_path: item.storage_path || '',
      featured: Boolean(item.featured),
      status: item.status || 'published',
      sort_order: item.sort_order || 0,
    });
    setModalOpen(true);
  };

  // Quick 1-click status toggle
  const handleToggleStatus = async (item, e) => {
    e.stopPropagation();
    try {
      await queries.gallery.toggleStatus(item.id, item.status);
      toast.success(
        `Gallery image set to ${item.status === 'published' ? 'Draft' : 'Published'}`
      );
      loadGallery();
    } catch (err) {
      toast.error('Failed to toggle status: ' + err.message);
    }
  };

  // Quick 1-click featured toggle
  const handleToggleFeatured = async (item, e) => {
    e.stopPropagation();
    try {
      await queries.gallery.toggleFeatured(item.id, item.featured);
      toast.success(`Gallery image ${!item.featured ? 'marked as Featured' : 'unmarked from Featured'}`);
      loadGallery();
    } catch (err) {
      toast.error('Failed to toggle featured: ' + err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormSaving(true);
    try {
      let finalImageUrl = formData.image_url;
      let finalStoragePath = formData.storage_path;

      if (selectedFile) {
        const uploadRes = await storage.uploadFile('gallery', selectedFile);
        finalImageUrl = uploadRes.publicUrl;
        finalStoragePath = uploadRes.storagePath;
      }

      if (!finalImageUrl) {
        toast.error('Please upload an image or provide a valid image URL');
        setFormSaving(false);
        return;
      }

      const payload = {
        ...formData,
        image_url: finalImageUrl,
        storage_path: finalStoragePath,
      };

      if (isEditing && selectedItem) {
        await queries.gallery.update(selectedItem.id, payload);
        toast.success('Gallery item updated in Supabase');
      } else {
        await queries.gallery.create(payload);
        toast.success('Gallery image added to Supabase');
      }

      setModalOpen(false);
      loadGallery();
    } catch (err) {
      console.error('Error saving gallery item:', err);
      toast.error('Failed to save gallery image: ' + err.message);
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    try {
      await queries.gallery.delete(selectedItem.id, selectedItem.storage_path);
      toast.success('Gallery image removed from Supabase');
      setConfirmOpen(false);
      setSelectedItem(null);
      loadGallery();
    } catch (err) {
      console.error('Error deleting gallery item:', err);
      toast.error('Failed to delete: ' + err.message);
    }
  };

  const filteredItems =
    categoryFilter === 'all'
      ? items
      : items.filter(
          (item) => item.category && item.category.toLowerCase() === categoryFilter.toLowerCase()
        );

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <Images size={24} className="text-[#9BB7A3]" />
            Gallery & Showcase Showcase
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Manage real-time portfolio assets displayed in the marquee slider and showcase grid.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 active:scale-95 shrink-0"
        >
          <Plus size={16} />
          <span>Upload Image</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs flex items-center gap-2.5">
          <AlertCircle size={16} className="shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}

      {/* Category Filter Pills */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setCategoryFilter('all')}
          className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition ${
            categoryFilter === 'all'
              ? 'bg-[#9BB7A3] text-slate-950 font-bold'
              : 'bg-slate-900 border border-slate-800 text-slate-400 hover:text-white'
          }`}
        >
          All Categories ({items.length})
        </button>
        {categories.map((cat) => {
          const count = items.filter(
            (i) => i.category && i.category.toLowerCase() === cat.toLowerCase()
          ).length;
          return (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition ${
                categoryFilter.toLowerCase() === cat.toLowerCase()
                  ? 'bg-[#9BB7A3] text-slate-950 font-bold'
                  : 'bg-slate-900 border border-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {cat} {count > 0 && `(${count})`}
            </button>
          );
        })}
      </div>

      {/* Gallery Cards Grid */}
      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="w-10 h-10 border-3 border-[#9BB7A3]/30 border-t-[#9BB7A3] rounded-full animate-spin"></div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-12 text-center max-w-md mx-auto">
          <Images size={40} className="mx-auto text-slate-600 mb-3" />
          <h3 className="text-base font-bold text-white mb-1">No Gallery Images</h3>
          <p className="text-xs text-slate-400 mb-6">
            {categoryFilter !== 'all'
              ? `No images found in category "${categoryFilter}".`
              : 'Upload your first showcase photo to populate the studio gallery.'}
          </p>
          <button
            onClick={handleOpenCreate}
            className="px-5 py-2.5 bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 text-xs font-bold rounded-xl transition"
          >
            Upload Photo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="group bg-slate-900 border border-slate-800 hover:border-[#9BB7A3]/40 rounded-2xl overflow-hidden shadow-lg transition-all duration-300 flex flex-col"
            >
              {/* Image Box */}
              <div className="relative aspect-[4/3] bg-slate-950 overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.title || 'Gallery Item'}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />

                {/* Status Badges Overlay */}
                <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
                  <span className="px-2.5 py-1 rounded-md text-[10px] font-bold bg-slate-950/80 text-slate-200 backdrop-blur-sm border border-slate-800">
                    {item.category || 'General'}
                  </span>
                  {item.featured && (
                    <span className="p-1 rounded-md bg-amber-500/80 text-slate-950 shadow-sm" title="Featured Item">
                      <Star size={11} className="fill-slate-950" />
                    </span>
                  )}
                </div>

                <div className="absolute top-2.5 right-2.5">
                  <button
                    onClick={(e) => handleToggleStatus(item, e)}
                    className="focus:outline-none transition active:scale-95"
                    title="Click to toggle Published / Draft"
                  >
                    <Badge variant={item.status === 'published' ? 'success' : 'warning'}>
                      {item.status === 'published' ? 'LIVE' : 'DRAFT'}
                    </Badge>
                  </button>
                </div>
              </div>

              {/* Info + Actions */}
              <div className="p-4 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="font-bold text-sm text-slate-100 line-clamp-1">
                    {item.title || 'Studio Showcase Asset'}
                  </h3>
                  {item.description && (
                    <p className="text-xs text-slate-400 line-clamp-2 mt-1">
                      {item.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-800/80">
                  <span className="text-[11px] font-mono text-slate-500">#{item.sort_order || 0}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => handleToggleFeatured(item, e)}
                      className={`p-1.5 rounded-lg transition ${
                        item.featured
                          ? 'text-amber-400 bg-amber-500/10'
                          : 'text-slate-400 hover:text-amber-400 hover:bg-slate-800'
                      }`}
                      title={item.featured ? 'Featured on Top' : 'Mark as Featured'}
                    >
                      <Star size={14} className={item.featured ? 'fill-amber-400' : ''} />
                    </button>
                    <button
                      onClick={() => handleOpenEdit(item)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-[#9BB7A3] hover:bg-slate-800 transition"
                      title="Edit Item"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedItem(item);
                        setConfirmOpen(true);
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-950/30 transition"
                      title="Delete Image"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => !formSaving && setModalOpen(false)}
        title={isEditing ? 'Edit Gallery Photo' : 'Upload Gallery Photo'}
        maxWidth="max-w-xl"
      >
        <form onSubmit={handleSave} className="space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Image Title / Caption
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g. Modern Minimalist TV Wall Console"
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Short description of the craftsmanship or materials"
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
            />
          </div>

          {/* Image Uploader */}
          <div className="bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80">
            <ImageUploader
              label="Gallery Image Asset"
              currentImageUrl={formData.image_url}
              value={formData.image_url}
              onImageSelected={(file) => setSelectedFile(file)}
              onImageRemoved={() => {
                setSelectedFile(null);
                setFormData((prev) => ({ ...prev, image_url: '' }));
              }}
              onChange={(url) => setFormData((prev) => ({ ...prev, image_url: url }))}
              recommendedSize="Recommended: High-res JPG/WEBP (up to 5MB)"
            />
          </div>

          {/* Featured Checkbox */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="gallery-featured"
              checked={formData.featured}
              onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
              className="w-4 h-4 rounded border-slate-800 text-[#9BB7A3] focus:ring-[#9BB7A3] bg-slate-950 cursor-pointer"
            />
            <label htmlFor="gallery-featured" className="text-xs text-slate-300 font-semibold cursor-pointer">
              Feature in Top Showcase Slider
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formSaving}
              className="px-6 py-2.5 rounded-xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 disabled:opacity-50"
            >
              {formSaving ? 'Saving to Database...' : isEditing ? 'Update Photo' : 'Upload Photo'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedItem(null);
        }}
        onConfirm={handleDelete}
        title="Delete Gallery Image"
        message="Are you sure you want to delete this gallery photo? It will be removed from the public website showcase."
        confirmText="Delete Photo"
        type="danger"
      />
    </div>
  );
}
